import { useState } from 'react'
import { ChevronDown, Cpu, ShieldCheck } from 'lucide-react'

import { ExampleBadge } from '../../ui/Badge'
import type { ExampleMachine } from './examples'

/**
 * The read-only capacity a user can look at before owning anything.
 *
 * Every row is marked twice over — a visible Example badge that is part of the
 * accessible name, and a note under the group saying what the whole surface is
 * — because "this is not your data" has to survive both a glance and a screen
 * reader. Nothing here is a price, a balance, or a status, so there is nothing
 * on this surface a tenant could read as a report about their own account.
 */

function ExampleRow({ machine }: { machine: ExampleMachine }) {
  const [open, setOpen] = useState(false)
  const detailId = `overview-example-${machine.id}`

  return (
    <li className="overview-example-row">
      <div className="overview-example-main">
        <span className={`overview-example-mark overview-example-mark-${machine.tone}`} aria-hidden="true">
          <Cpu size={20} strokeWidth={1.8} />
        </span>

        <div className="overview-example-name">
          <span className="overview-example-title">
            <strong>{machine.name}</strong>
            <ExampleBadge />
          </span>
          <p>{machine.summary}</p>
        </div>

        <div className="overview-example-specs">
          {machine.specs.map((spec) => (
            <span key={spec.label} className="overview-example-spec">
              <strong>{spec.value}</strong>
              <span>{spec.label}</span>
            </span>
          ))}
        </div>

        <button
          type="button"
          className="overview-example-toggle"
          aria-expanded={open}
          aria-controls={detailId}
          onClick={() => setOpen((value) => !value)}
        >
          {open ? 'Hide details' : 'View details'}
          {/* The name says which machine, so the label is not repeated per row. */}
          <span className="sr-only"> for {machine.name}, example</span>
          <ChevronDown size={16} aria-hidden="true" className={open ? 'overview-example-caret-open' : ''} />
        </button>
      </div>

      {/* Progressive disclosure: the first screen shows a shape, not a manual. */}
      <div id={detailId} className="overview-example-detail" hidden={!open}>
        <p>{machine.detail}</p>
        <ul>
          {machine.suits.map((use) => (
            <li key={use}>{use}</li>
          ))}
        </ul>
        <p className="field-help">Example only. Connect a wallet to rent capacity like this.</p>
      </div>
    </li>
  )
}

export function ExampleMachines({ machines }: { machines: readonly ExampleMachine[] }) {
  return (
    <section className="overview-examples" aria-labelledby="overview-examples-title">
      <div className="overview-section-head">
        <div>
          <h2 id="overview-examples-title">Explore example machines</h2>
          <p>Safe, read-only examples to see DDWS in action.</p>
        </div>
      </div>

      <div className="overview-surface overview-example-surface">
        {/* Column names at desktop only. Every value carries its own label in
            the row, so this strip is presentation and stays out of the tree. */}
        <div className="overview-example-head" aria-hidden="true">
          <span />
          <span>Example machine</span>
          <span className="overview-example-specs">
            <span>vCPU</span>
            <span>RAM</span>
            <span>Storage</span>
            <span>Region</span>
          </span>
          <span />
        </div>
        <ul className="overview-example-list">
          {machines.map((machine) => (
            <ExampleRow key={machine.id} machine={machine} />
          ))}
        </ul>
      </div>

      <p className="inline-note inline-note-example">
        <ShieldCheck size={18} aria-hidden="true" />
        Examples are read-only and use sample data. No wallet is needed to explore.
      </p>
    </section>
  )
}
